import { WebStorageStateStore } from 'oidc-client-ts';
import { useEffect } from 'react';
import { AuthProvider, AuthProviderProps, useAuth as useOidcAuth } from 'react-oidc-context';
import { FourOOne } from 'src/pages/ErrorPage/401';
import { LoadingPage } from 'src/pages/LoadingPage';
import { useGetAuthConfig } from './useGetAuthConfig';
import { StorageKeys } from 'src/utils';

const OIDC_USER_STORAGE_PREFIX = 'oidc.user';
// Note: Implemented to have more control over the use of "id_token" via the "getAccessToken" method.
// This is required to validate against the source of truth i.e. the browser-level storage for valid OIDC key.
// Without a valid OIDC key, we consider the session not authenticated.
export function useAuth() {
  const { oauth } = useGetAuthConfig();

  const auth = useOidcAuth();
  return {
    isLoading: auth.isLoading,
    isAuthenticated: auth.isAuthenticated,
    getAccessToken: () => {
      // Note: This condition is due to cognito not being fully oauth compatible.
      if (!oauth.authority) {
        return auth.user?.access_token;
      }
      const aud = auth.user?.profile?.aud;
      const iss = auth.user?.profile?.iss;
      if (iss && aud && typeof aud === 'string') {
        if (window.localStorage.getItem(`${OIDC_USER_STORAGE_PREFIX}:${iss}:${aud}`)) {
          return auth.user?.id_token;
        }
      }
      return undefined;
    },
    logout: async () => {
      // Note: This condition is due to cognito not being fully oauth compatible.
      if (!oauth.authority) {
        await auth.signoutRedirect();
      } else {
        await auth.removeUser();
        await auth.revokeTokens();
        await auth.clearStaleState();
        window.location.href = `${oauth.logoutUri}?client_id=${auth.settings.client_id}&logout_uri=${auth.settings.post_logout_redirect_uri}&redirect_uri=${auth.settings.redirect_uri}&response_type=code`;
      }
    },
  };
}

function AutoSignIn(props: { children: JSX.Element }) {
  const auth = useOidcAuth();

  useEffect(() => {
    if (!auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading) {
      auth.signinRedirect();
    }
  }, [auth]);

  if (auth.error) {
    return <FourOOne />;
  }

  if (auth.isLoading || !auth.isAuthenticated) {
    return <LoadingPage />;
  }

  if (auth.isAuthenticated) {
    window.localStorage.setItem(StorageKeys.ACCESS_TOKEN, auth.user?.access_token as string);
    window.localStorage.setItem(StorageKeys.ID_TOKEN, auth.user?.id_token as string);
    window.localStorage.setItem(StorageKeys.REFRESH_TOKEN, auth.user?.refresh_token as string);
  }

  return props.children;
}

export function Authenticator(props: { children: JSX.Element }) {
  const { oauth, isLoading } = useGetAuthConfig();

  if (isLoading) {
    return <LoadingPage />;
  }

  const authConfig: AuthProviderProps = {
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    authority: oauth.authority || oauth.issuer || '',
    client_id: oauth.clientId || '',
    redirect_uri: `${window.location.protocol}//${window.location.host}${oauth.authority ? '/login' : ''}`,
    post_logout_redirect_uri: `${window.location.protocol}//${window.location.host}${oauth.authority ? '/login' : ''}`,
    revokeTokensOnSignout: true,
  };

  return (
    <AuthProvider {...authConfig}>
      <AutoSignIn>{props.children}</AutoSignIn>
    </AuthProvider>
  );
}
